import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, MapPin, Star, ArrowRight, X, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { useFavorites } from '../hooks/useFavorites';
import { fetchPopularStations, searchStations } from '../api/client';

const POPULAR_NAMES = ['Strasbourg', 'Metz Ville', 'Nancy Ville', 'Reims', 'Mulhouse Ville', 'Thionville'];

const FALLBACK_IDS = {
  'Strasbourg':       { id: 'stop_area:SNCF:87212027', name: 'Strasbourg',     region: 'Bas-Rhin' },
  'Metz Ville':       { id: 'stop_area:SNCF:87192039', name: 'Metz Ville',     region: 'Moselle' },
  'Nancy Ville':      { id: 'stop_area:SNCF:87141002', name: 'Nancy Ville',    region: 'Meurthe-et-Moselle' },
  'Reims':            { id: 'stop_area:SNCF:87171009', name: 'Reims',          region: 'Marne' },
  'Mulhouse Ville':   { id: 'stop_area:SNCF:87182063', name: 'Mulhouse Ville', region: 'Haut-Rhin' },
  'Thionville':       { id: 'stop_area:SNCF:87191007', name: 'Thionville',     region: 'Moselle' },
};

const StationPicker = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [popular, setPopular] = useState([]);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const { favorites, toggleFavorite, isFavorite } = useFavorites();

  useEffect(() => {
    let alive = true;
    fetchPopularStations().then((list) => { if (alive) setPopular(list || []); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const list = await searchStations(query.trim(), 10);
        setResults(list || []);
        setHighlight(0);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const displayList = query.trim() ? results : popular;

  const select = (station) => {
    setQuery(station.name);
    setOpen(false);
    const params = new URLSearchParams({ gare: station.name, id: station.id });
    navigate(`/tableau?${params.toString()}`);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, displayList.length - 1)); setOpen(true); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      const choice = displayList[highlight];
      if (choice) select(choice);
    }
    else if (e.key === 'Escape') setOpen(false);
  };

  const selectByName = (name) => {
    const found = popular.find((s) => s.name === name)
      || popular.find((s) => s.name.toLowerCase().startsWith(name.toLowerCase()));
    if (found) { select(found); return; }
    if (FALLBACK_IDS[name]) { select(FALLBACK_IDS[name]); return; }
    searchStations(name, 1).then((list) => { if (list && list[0]) select(list[0]); });
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="flex items-center gap-2 bg-white rounded-xl shadow-lg border border-slate-200 p-2">
        <div className="flex items-center gap-2 flex-1 px-3">
          <Search className="w-5 h-5 text-slate-400" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            placeholder={t('picker.placeholder')}
            className="bg-transparent border-0 outline-none text-base h-12 w-full text-slate-900 placeholder:text-slate-400"
          />
          {searching && <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />}
          {query && !searching && (
            <button onClick={() => { setQuery(''); inputRef.current?.focus(); }} className="text-slate-400 hover:text-slate-700">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <Button
          onClick={() => displayList[highlight] && select(displayList[highlight])}
          disabled={!displayList[highlight]}
          className="h-12 px-6 bg-ter-blue hover:bg-ter-blue-dark text-white font-semibold disabled:opacity-50"
        >
          {t('picker.button')} <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>

      {open && (
        <div className="absolute z-30 w-full mt-2 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden slide-in">
          {displayList.length === 0 && !searching ? (
            <div className="px-4 py-6 text-center text-sm text-slate-500">
              {query.trim() ? t('picker.no_match', { q: query }) : t('picker.empty')}
            </div>
          ) : (
            <ul className="max-h-80 overflow-auto scrollbar-thin">
              {displayList.map((s, idx) => {
                const active = idx === highlight;
                return (
                  <li key={s.id || s.name + idx}>
                    <button
                      onMouseEnter={() => setHighlight(idx)}
                      onMouseDown={(e) => { e.preventDefault(); select(s); }}
                      className={`w-full flex items-center justify-between px-4 py-3 border-b border-slate-100 last:border-0 text-left transition-colors ${active ? 'bg-slate-50' : 'hover:bg-slate-50'}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-md flex items-center justify-center ${active ? 'bg-ter-blue text-white' : 'bg-slate-100 text-ter-blue'}`}>
                          <MapPin className="w-4 h-4" />
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900">{s.name}</div>
                          <div className="text-xs text-slate-500">{s.region || '—'}</div>
                        </div>
                      </div>
                      <span
                        role="button"
                        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavorite(s.name); }}
                      >
                        <Star className={`w-5 h-5 transition-colors ${isFavorite(s.name) ? 'fill-ter-yellow text-ter-yellow' : 'text-slate-300 hover:text-ter-yellow'}`} />
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="px-4 py-2 bg-slate-50 border-t border-slate-100 text-[11px] text-slate-500 flex items-center justify-between">
            <span>{t('picker.kbd_hint')}</span>
            <span className="font-semibold text-ter-blue">
              {query.trim() ? t('picker.navitia_search') : t('picker.popular_count', { count: popular.length })}
            </span>
          </div>
        </div>
      )}

      <div className="mt-6">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">{t('picker.popular_title')}</div>
        <div className="flex flex-wrap gap-2">
          {POPULAR_NAMES.map(name => (
            <button
              key={name}
              onClick={() => selectByName(name)}
              className="px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-800 hover:border-ter-blue hover:bg-ter-blue hover:text-white transition-colors text-sm font-medium"
            >
              {name}
            </button>
          ))}
        </div>
      </div>

      {favorites.length > 0 && (
        <div className="mt-6">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3 flex items-center gap-2">
            <Star className="w-3.5 h-3.5 fill-ter-yellow text-ter-yellow" /> {t('picker.favorites_title')}
          </div>
          <div className="flex flex-wrap gap-2">
            {favorites.map(name => (
              <div key={name} className="flex items-center gap-1 bg-ter-blue text-white rounded-full overflow-hidden">
                <button onClick={() => selectByName(name)} className="px-4 py-2 text-sm font-medium hover:bg-ter-blue-dark">{name}</button>
                <button onClick={() => toggleFavorite(name)} className="px-2 py-2 hover:bg-ter-blue-dark border-l border-white/20">
                  <Star className="w-4 h-4 fill-ter-yellow text-ter-yellow" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StationPicker;
